import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { get } from "../lib/api";
import { useDebounced, useDocumentTitle, useLoad } from "../lib/hooks";
import { SEARCH_STATUSES, SEARCH_TYPES, resultPath, searchIcon, summaryText } from "../lib/reports";
import { Card, Chip, Empty, ErrorBox, Input, Loading, PageHead, Select } from "../ui";
import { HiddenLabel } from "../components/Hidden";

const TIERS = [
    { value: "A", label: "Tier A" },
    { value: "B", label: "Tier B" },
    { value: "C", label: "Tier C" },
    { value: "none", label: "No tier" },
];

const typeLabel = (t) => (SEARCH_TYPES.find((x) => x.value === t) || { label: t }).label;

function useFilters() {
    const location = useLocation();
    const history = useHistory();
    const p = new URLSearchParams(location.search);
    const f = {
        q: p.get("q") || "",
        type: p.get("type") || "",
        tier: p.get("tier") || "",
        tag: p.get("tag") || "",
        status: p.get("status") || "",
    };
    const set = (next) => {
        const v = { ...f, ...next };
        const out = new URLSearchParams();
        Object.keys(v).forEach((k) => v[k] && out.set(k, v[k]));
        history.replace({ search: out.toString() ? `?${out}` : "" });
    };
    return [f, set];
}

// Cross-entity search over GET /v1/crm/search (E9).
export default function Search() {
    useDocumentTitle("Search");
    const [f, set] = useFilters();
    const [text, setText] = useState(f.q);
    const [tag, setTag] = useState(f.tag);
    const dq = useDebounced(text.trim(), 250);
    const dtag = useDebounced(tag.trim(), 400);

    useEffect(() => {
        if (dq !== f.q) set({ q: dq });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dq]);
    // The palette can change ?q= while this page is open.
    useEffect(() => {
        if (f.q !== dq) setText(f.q);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [f.q]);
    useEffect(() => {
        if (dtag !== f.tag) set({ tag: dtag });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dtag]);

    const hasQuery = !!(f.q || f.tier || f.tag || f.status);
    const { data, error, loading, reload } = useLoad(
        () => (hasQuery ? get("v1/crm/search", { q: f.q, type: f.type, tier: f.tier, tag: f.tag, status: f.status, limit: 100 }) : Promise.resolve(null)),
        [f.q, f.type, f.tier, f.tag, f.status]
    );
    const rows = (data && data.data) || [];
    const byType = rows.reduce((m, r) => ({ ...m, [r.type]: (m[r.type] || 0) + 1 }), {});
    const groups = SEARCH_TYPES.filter((t) => byType[t.value]);

    return (
        <>
            <PageHead title="Search" sub={data ? `${data.count} ${data.count === 1 ? "result" : "results"}${data.count > rows.length ? `, first ${rows.length} shown` : ""}` : "People, companies, leads and signals"} />
            <Card noBody>
                <div className="hz-card-section">
                    <div className="hz-search" style={{ maxWidth: 640 }}>
                        <i className="ri-search-line"></i>
                        <Input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Name, email, LinkedIn URL, domain, lead title, signal detail…" aria-label="Search" />
                    </div>
                </div>
                <div className="hz-filterbar">
                    <Chip active={!f.type} onClick={() => set({ type: "" })}>All</Chip>
                    {SEARCH_TYPES.map((t) => (
                        <Chip key={t.value} icon={t.icon} active={f.type === t.value} onClick={() => set({ type: f.type === t.value ? "" : t.value })}>
                            {t.label}
                        </Chip>
                    ))}
                    <span className="hz-spacer"></span>
                    <Select small aria-label="Tier" value={f.tier} placeholder="Any tier" options={TIERS} onChange={(e) => set({ tier: e.target.value })} />
                    <select className="hz-select hz-select-sm" aria-label="Status" value={f.status} onChange={(e) => set({ status: e.target.value })} style={{ width: "auto", minWidth: 140 }}>
                        <option value="">Any status</option>
                        {["Due", "Leads", "Signals"].map((g) => (
                            <optgroup key={g} label={g}>
                                {SEARCH_STATUSES.filter((s) => s.group === g).map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    <Input small value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Tag, e.g. segment:fintech" aria-label="Tag" style={{ width: 200 }} />
                </div>
                {(f.tier || f.tag) && (
                    <div className="hz-small hz-muted" style={{ padding: "8px 16px 0" }}>
                        {f.tier ? "Tier applies to people only. " : ""}
                        {f.tag ? "Signals have no tags and are left out." : ""}
                    </div>
                )}
                <ErrorBox error={error} onRetry={reload} />
                {!hasQuery && <Empty>Type to search, or pick a tier, status or tag.</Empty>}
                {hasQuery && loading && !data && <Loading text="Searching…" />}
                {hasQuery && data && rows.length === 0 && <Empty>No matches.</Empty>}
                {groups.map((g) => (
                    <div key={g.value}>
                        <div className="hz-subhead">
                            {typeLabel(g.value)} <span className="hz-muted">· {byType[g.value]}</span>
                        </div>
                        {rows
                            .filter((r) => r.type === g.value)
                            .map((r) => (
                                <Link key={r.type + r.id} to={resultPath(r)} className="hz-list-item">
                                    <span className="hz-tl-icon"><i className={searchIcon(r.type)}></i></span>
                                    <div className="hz-spacer" style={{ minWidth: 0 }}>
                                        <div className="hz-row" style={{ gap: 6 }}>
                                            <span className="hz-name">{r.title}</span>
                                            <HiddenLabel at={r.hidden_at} />
                                        </div>
                                        <div className="hz-small hz-muted hz-ellipsis">{summaryText(r.summary)}</div>
                                    </div>
                                    <i className="ri-arrow-right-s-line hz-muted"></i>
                                </Link>
                            ))}
                    </div>
                ))}
            </Card>
        </>
    );
}
